import { useEffect } from "react";

/** Turns the tap diagnostic on (`"1"`) for this browser. Temporary: it checks on a real iPhone where a tap
 *  on the tray's Send lands with the keyboard up. */
export const TAP_DEBUG_KEY = "kory.debug.tap";
/** `?kory-debug-tap=1` (or `=0`) sets the key from the address bar, for a phone with no console. */
const TAP_DEBUG_PARAM = "kory-debug-tap";
const LINES = 6;

/** An element named by its tag and its own labels; never its text, so no message or name is shown. */
function describe(el: Element | null): string {
  if (!el) return "none";
  const label = el.getAttribute("aria-label");
  const testId = el.getAttribute("data-testid");
  return [el.tagName.toLowerCase(), label && `"${label}"`, testId && `#${testId}`].filter(Boolean).join(" ");
}

const round = (n: number) => Math.round(n);

/** One line per pointerdown and click: where it landed, against where Send and the visual viewport are. */
export function tapLine(event: PointerEvent | MouseEvent): string {
  const target = event.target instanceof Element ? event.target : null;
  const send = document.querySelector("[data-kory-send]");
  const tray = document.querySelector('[data-testid="global-kory-tray"]');
  const r = send?.getBoundingClientRect();
  const vv = window.visualViewport;
  return [
    `${event.type} ${describe(target)}`,
    `onSend=${Boolean(target && send?.contains(target))} inTray=${Boolean(target && tray?.contains(target))}`,
    `at=${round(event.clientX)},${round(event.clientY)}`,
    r ? `send=${round(r.left)},${round(r.top)} ${round(r.width)}x${round(r.height)}` : "send=none",
    `vv.top=${round(vv?.offsetTop ?? 0)} vv.h=${round(vv?.height ?? window.innerHeight)}`,
    `innerH=${window.innerHeight} scrollY=${round(window.scrollY)} focus=${describe(document.activeElement)}`,
  ].join(" | ");
}

/** While `kory.debug.tap` is "1": each pointerdown and click on the page is logged to the console and shown in a
 *  small box at the top of what's visible. Off by default; nothing is sent anywhere. */
export function useTrayTapDebug(): void {
  useEffect(() => {
    try {
      const asked = new URLSearchParams(window.location.search).get(TAP_DEBUG_PARAM);
      if (asked === "1") localStorage.setItem(TAP_DEBUG_KEY, "1");
      if (asked === "0") localStorage.removeItem(TAP_DEBUG_KEY);
      if (localStorage.getItem(TAP_DEBUG_KEY) !== "1") return;
    } catch {
      return;
    }
    const box = document.createElement("pre");
    box.setAttribute("data-kory-tap-debug", "");
    box.setAttribute("aria-hidden", "true");
    Object.assign(box.style, {
      position: "fixed",
      left: "0",
      right: "0",
      zIndex: "2147483647",
      margin: "0",
      padding: "4px",
      font: "10px/1.3 ui-monospace, monospace",
      whiteSpace: "pre-wrap",
      background: "rgba(0,0,0,0.75)",
      color: "#fff",
      pointerEvents: "none",
    });
    document.body.appendChild(box);
    const lines: string[] = [];
    const log = (event: Event) => {
      const line = tapLine(event as PointerEvent);
      console.info(`[kory tap] ${line}`);
      lines.push(line);
      if (lines.length > LINES) lines.shift();
      box.textContent = lines.join("\n");
      // At the top of what shows: with the keyboard up Safari may have panned the page.
      box.style.top = `${round(window.visualViewport?.offsetTop ?? 0)}px`;
    };
    const options = { capture: true, passive: true } as const;
    document.addEventListener("pointerdown", log, options);
    document.addEventListener("click", log, options);
    return () => {
      document.removeEventListener("pointerdown", log, options);
      document.removeEventListener("click", log, options);
      box.remove();
    };
  }, []);
}
